import { useEffect, useRef, useState } from "react";
import { RouteState } from "../../../api/edits/editRoute.responses";
import Walker from "./Walker";

export type PoiPlaybackState = {
  poiName: string;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
};

type UsePoiPlaybackResult = {
  poiState: PoiPlaybackState | null;
  onTogglePlayPause: () => void;
  onSkipForward: () => void;
};

export function usePoiPlayback(
  walkerRef: React.MutableRefObject<Walker>,
  routeState: RouteState,
  isSimulating: boolean
): UsePoiPlaybackResult {
  const [poiState, setPoiState] = useState<PoiPlaybackState | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isSimulating) {
      setPoiState(null);
      return;
    }

    intervalRef.current = setInterval(() => {
      const walker = walkerRef.current;
      const playback = walker.GetCurrentPOIPlayback();
      if (playback === null) return;

      const poi = routeState.pointsOfInterest[walker.currentPoiIndex];
      const poiName = poi
        ? Object.values(poi.pointOfInterestTranslations)[0]?.name ?? `POI ${walker.currentPoiIndex + 1}`
        : `POI ${walker.currentPoiIndex + 1}`;

      setPoiState({
        poiName,
        currentTime: playback.current,
        duration: playback.duration,
        isPlaying: playback.isPlaying,
      });
    }, 500);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isSimulating, routeState]);

  const onTogglePlayPause = () => {
    walkerRef.current.TogglePOIPause();
  };

  const onSkipForward = () => {
    walkerRef.current.SkipForwardPOI();
  };

  return { poiState, onTogglePlayPause, onSkipForward };
}
